import React, { Suspense, useContext, useEffect, useState } from 'react';
import { GridLoader } from 'react-spinners';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Link, Route, Routes } from 'react-router-dom';
import { chakra, ChakraProvider, Text } from '@chakra-ui/react';
import { theme } from './ChakraTheme';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from '../consts/styled';
import { zIndex, colors, spacing } from './ChakraTheme';
import { CloudinaryContext } from 'cloudinary-react';
import { NavContainer } from '../components/Navigation/NavContainer';
import { GuidesPage, UnitPage, ArticlePage } from '../modules/app';
import { SignInPage, SignOutPage } from '../modules/auth';

import Page404 from '../general-pages/Page404';
import AuthProvider, { AuthContext } from '../context/AuthProvider';
import Navigation from '../modules/navigation';
import ForbiddenPage from '../general-pages/ForbiddenPage';
import ScrollToTop from '../modules/navigation/ScrollToTop';

import ChatContainer from './ChatContainer';
import { DashboardPage, ClassPage } from '../modules/app';

import { trackAction } from '../helpers/trackAction';
import { axiosWrapper } from '../hooks/axios';
import ModalContainer from '../components/ModalContainer';
import Help from '../components/Help';
import Changelog from '../components/Changelog';
import InfoMainPage from '../modules/info/InfoMainPage';
import ProgramInfoPage from '../modules/info/ProgramInfoPage';
import InteractiveTestPage from '../modules/app/pages/InteractiveTest/InteractiveTestPage';
import SignUpPage from '../modules/auth/pages/SignUpPage';

const LazyActivateConfirmedPage = React.lazy(() => import('../modules/auth/pages/ActivateConfirmedPage'));
const LazyActivateSetPasswordPage = React.lazy(() => import('../modules/auth/pages/ActivateSetPasswordPage'));
const LazyInviteCodeHomePage = React.lazy(() => import('../modules/auth/pages/InviteCodePage'));
const LazyInviteCodePage = React.lazy(() => import('../modules/auth/pages/InviteCodePage/show'));
const LazyPasswordResetCompletePage = React.lazy(() => import('../modules/auth/pages/PasswordResetCompletePage'));
const LazyPasswordResetDonePage = React.lazy(() => import('../modules/auth/pages/PasswordResetDonePage'));
const LazyPasswordResetPage = React.lazy(() => import('../modules/auth/pages/PasswordResetPage'));
const LazyPasswordResetSetPasswordPage = React.lazy(() => import('../modules/auth/pages/PasswordResetSetPasswordPage'));

const LazyDemoSignUpPage = React.lazy(() => import('../modules/app/pages/DemoSignUp'));
const LazySignUpEvolveMeStudyGroupPage = React.lazy(() => import('../modules/app/pages/SignUpEvolveMeStudyGroup'));
const LazyPrivacyPage = React.lazy(() => import('../modules/legal/PrivacyPage'));
const LazyTermsPage = React.lazy(() => import('../modules/legal/TermsPage'));

const LazyEditStudyGroupPage = React.lazy(() => import('../modules/app/pages/Licenses/edit/'));
const LazyDeleteStudentsPage = React.lazy(() => import('../modules/app/pages/Licenses/DeleteStudentsPage/'));
const LazyManageDevicesPage = React.lazy(() => import('../modules/app/pages/Licenses/ManageDevicesPage'));
const LazyStaffPanelPage = React.lazy(() => import('../modules/app/pages/StaffPanel/'));
const LazyLicensesPage = React.lazy(() => import('../modules/app/pages/Licenses/index'));
const LazySettingsPage = React.lazy(() => import('../modules/auth/pages/SettingsPage'));
const LazyProfilePage = React.lazy(() => import('../modules/app/pages/Profile'));
const LazyTrackEditPage = React.lazy(() => import('../modules/app/pages/StaffPanel/subpages/TrackEditPage'));
const LazyDemoAskInfoPage = React.lazy(() => import('../modules/auth/pages/DemoAskInfoPage'));

const LazyEventLogPage = React.lazy(() => import('../modules/app/pages/StaffPanel/EventLogPage'));
const LazyMyPrograms = React.lazy(() => import('../modules/app/pages/MyPrograms'));
const LazyAssistants = React.lazy(() => import('../modules/app/pages/Assistants'));
const LazyLicenseExpired = React.lazy(() => import('../modules/app/pages/Licenses/LicenseExpired'));
const LazySignUpPage = React.lazy(() => import('../modules/auth/pages/SignUpPage'));

const ModalGlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
  }

  #universe {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  div[data-tippy-root] {
    z-index: ${zIndex.MODAL + 1} !important;
  }

  .max-lines {
    -webkit-box-orient: vertical !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 3 !important;
    overflow: hidden !important;
    padding-bottom: 0 !important;
  }
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const App = styled(({ className }) => {
  const user = useContext(AuthContext);
  const { t } = useTranslation();
  const [interval, setSetInterval] = useState<any>();

  useEffect(() => {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const viewport = { width: vw, height: vh };

    trackAction(
      {
        action_type: 'app_load',
        data: {
          viewport,
          userAgent: navigator.userAgent,
        },
      },
      user?.user_actions,
    );

    if (interval !== undefined && interval !== null) {
      clearInterval(interval);
      setSetInterval(null);
    }
    if (document.hasFocus()) {
      const url = window.location.pathname;
      const top = document.scrollingElement?.scrollTop || 0;
      const left = document.scrollingElement?.scrollLeft || 0;
      axiosWrapper('/api/v1/records/heartbeat/', 'post', { url, scroll: { top, left } });
    }
    setSetInterval(
      setInterval(() => {
        if (document.hasFocus()) {
          const url = window.location.pathname;
          const top = document.scrollingElement?.scrollTop || 0;
          const left = document.scrollingElement?.scrollLeft || 0;
          axiosWrapper('/api/v1/records/heartbeat/', 'post', { url, scroll: { top, left } });
        }
      }, 2 * 60000), // 2 minutes
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className={className}>
      <ChakraProvider theme={theme}>
        <AuthProvider>
          <DndProvider backend={HTML5Backend}>
            <CloudinaryContext cloudName="dtg18yyuk">
              <ModalGlobalStyle />
              <Helmet>
                <meta name="viewport" content="initial-scale=1,maximum-scale=1,minimum-scale=1,width=device-width" />
              </Helmet>
              <Router>
                <div className="main">
                  <Navigation hideSecondRow={false} />
                  <ScrollToTop ignored={[UnitPage.unit.getRoute(), UnitPage.content.getRoute(), UnitPage.module.getRoute(), ClassPage.getRoute()]} />
                  <Suspense fallback={<div>Loading...</div>}>
                    <Routes>
                      {/* <Route
                      path="/foobar"
                      element={() => {
                        // @ts-ignore
                        return <button onClick={() => noMethod}>This breaks stuff</button>;
                      }}
                    /> */}
                      <Route path={DashboardPage.getRoute()} element={<DashboardPage />} />
                      <Route path={'/demo-info/'} element={<LazyDemoAskInfoPage />} />
                      <Route path={'/demo-sign-up/:demoCode/'} element={<LazyDemoSignUpPage />} />
                      <Route path={'/sign-up/evolveme/'} element={<LazySignUpEvolveMeStudyGroupPage />} />
                      <Route path={'/licenses/:licenseId'} element={<LazyEditStudyGroupPage />} />
                      <Route path={'/unenrolled-students/:schoolId/'} element={<LazyDeleteStudentsPage />} />
                      <Route path={'/manage-devices/:schoolId?/'} element={<LazyManageDevicesPage />} />
                      <Route path={'/staffpanel/:activeTab?/'} element={<LazyStaffPanelPage />} />
                      <Route path={'/staffpanel/tracks/edit/:trackId/'} element={<LazyTrackEditPage />} />
                      <Route path={'/licenses/'} element={<LazyLicensesPage />} />
                      <Route path={GuidesPage.getRoute()} element={<GuidesPage />} />
                      <Route path={ClassPage.getRoute()} element={<ClassPage />} />
                      <Route path={UnitPage.unit.getRoute()} element={<UnitPage />} />
                      <Route path={UnitPage.module.getRoute()} element={<UnitPage />} />
                      <Route path={UnitPage.content.getRoute()} element={<UnitPage />} />
                      <Route path={(ArticlePage as any).getRoute()} element={<ArticlePage />} />
                      <Route path={SignInPage.getRoute()} element={<SignInPage />} />
                      <Route path={SignUpPage.getRoute()} element={<LazySignUpPage />} />
                      <Route path={'/password_reset/'} element={<LazyPasswordResetPage />} />
                      <Route path={'/settings/'} element={<LazySettingsPage />} />
                      <Route path={'/profile'} element={<LazyProfilePage />} />
                      <Route path={'/event-log/:userId?/'} element={<LazyEventLogPage />} />
                      <Route path={'/password_reset/done/'} element={<LazyPasswordResetDonePage />} />
                      <Route path={'/password_reset/:uid/:token/'} element={<LazyPasswordResetSetPasswordPage />} />
                      <Route path={'/password_reset/complete/'} element={<LazyPasswordResetCompletePage />} />
                      <Route path={ForbiddenPage.getRoute()} element={<ForbiddenPage />} />
                      <Route path={'/activate/:hash/:uid/:token/'} element={<LazyActivateSetPasswordPage />} />
                      <Route path={'/activate/:hash/done/'} element={<LazyActivateConfirmedPage />} />
                      <Route path={'/join/:code'} element={<LazyInviteCodePage />} />
                      <Route path={'/join/'} element={<LazyInviteCodeHomePage />} />
                      <Route path={'/info'} element={<InfoMainPage />} />
                      <Route path={'/explore'} element={<InfoMainPage />} />
                      <Route path={'/experience'} element={<InfoMainPage />} />
                      <Route path={'/info/:id'} element={<ProgramInfoPage />} />
                      <Route path={'/explore/:slug'} element={<ProgramInfoPage />} />
                      <Route path={'/experience/:slug'} element={<ProgramInfoPage />} />
                      <Route path={'/myprograms'} element={<LazyMyPrograms />} />
                      <Route path={'/assistants'} element={<LazyAssistants />} />
                      <Route path={'/assistants/:id'} element={<LazyAssistants />} />
                      <Route path={'/license-expired'} element={<LazyLicenseExpired />} />
                      <Route
                        path={SignOutPage.getRoute()}
                        element={
                          <>
                            <AuthContext.Consumer>
                              {(value) =>
                                value?.is_authenticated ? (
                                  <LoaderContainer>
                                    <GridLoader color={colors.darkblue} />
                                  </LoaderContainer>
                                ) : (
                                  <SignOutPage />
                                )
                              }
                            </AuthContext.Consumer>
                          </>
                        }
                      />
                      <Route path={'/legal/privacy'} element={<LazyPrivacyPage />} />
                      <Route path={'/legal/terms'} element={<LazyTermsPage />} />
                      <Route path={Page404.getRoute()} element={<Page404 />} />
                      <Route path={'/interactive-test/'} element={<InteractiveTestPage />} />
                      <Route path="*" element={<Page404 />} />
                    </Routes>
                  </Suspense>
                  <ModalContainer />
                </div>

                <div className="footer">
                  <chakra.hr opacity={0.2} color="#8e8fa2" style={{ marginBottom: spacing.SMALL }} />
                  <Text textStyle="finePrint" color="#8e8fa2">
                    {t('app.copyright')} {new Date().getFullYear()}. {t('app.rightsReserved')}{' '}
                    <Link className="pdf-link" to="/legal/privacy" target="_blank">
                      {t('app.privacyPolicy')}
                    </Link>
                    {' or '}
                    <Link className="pdf-link" to="/legal/terms" target="_blank">
                      {t('app.termsOfUse')}
                    </Link>
                    <Changelog />
                    <Help />
                  </Text>
                </div>
                <ChatContainer />
              </Router>
            </CloudinaryContext>
          </DndProvider>
        </AuthProvider>
      </ChakraProvider>
    </div>
  );
})`
  * {
    box-sizing: border-box;
  }

  max-width: 2000px;
  margin: 0 auto;

  min-height: 100vh;

  ${NavContainer} {
    max-width: 2000px;
    width: 100% !important;
    padding-left: ${spacing.BASE_PLUS};
    padding-right: ${spacing.BASE_PLUS};
    margin-left: -${spacing.BASE};
    margin-right: -${spacing.BASE};
    &.marketplace {
      padding-left: 0px;
      padding-right: 0px;
      margin-left: 0px;
      margin-right: 0px;
    }
  }

  .main {
    min-height: calc(100vh - 60px);
  }

  .footer {
    width: 90%;
    margin: 0 auto;
    height: 60px;

    .pdf-link {
      color: inherit;
      text-decoration: underline;
    }
  }
`;

export default App;
